import { useEffect } from 'react';
import { useAccountPositionStore } from '../../Store/accountPositionStore';
import { useAuth } from '../../Context/AuthProvider';

/**
 * Custom hook to handle WebSocket updates for account operations
 * Listens for events related to account creation, updates, and deletions
 */
export const useAccountWebSocketUpdates = () => {
  const { 
    accounts, 
    selectedAccount, 
    setSelectedAccount, 
    setError,
    fetchAccountPositions,
    positions
  } = useAccountPositionStore();
  
  const { token, userId } = useAuth(); // Get userId from auth context

  // Helper: ensure loaders are not shown for WebSocket updates
  const clearLoading = () => {
    try {
      useAccountPositionStore.setState({ isLoading: false });
    } catch {}
  };

  useEffect(() => {
    // Notify WebSocket layer of active account change
    if (selectedAccount) {
      window.selectedAccountId = selectedAccount;
      localStorage.setItem('selectedAccountId', selectedAccount);
      window.dispatchEvent(new CustomEvent('activeAccountChanged', { detail: selectedAccount }));
      // [updating account ws] Log active account change
      console.log(`[updating account ws] Active account changed: ${selectedAccount}`);
    }
  }, [selectedAccount]);

  useEffect(() => {
    // Handler for account update events (creation, modification, and deletion if isActive === 0)
    const handleAccountUpdate = (event) => {
      // Clear loaders when WS update arrives
      clearLoading();

      const updatedAccount = event.detail;
      console.log('[updating account ws] Received accountUpdate event:', updatedAccount);
      
      // Accept either accountId or accountMstId as the identifier
      const accountIdentifier = updatedAccount.accountId || updatedAccount.accountMstId;

      if (!updatedAccount || !accountIdentifier) {
        console.log('[updating account ws] Invalid account update data received:', updatedAccount);
        return;
      }
      
      // If isActive is 0, treat as deletion
      if (updatedAccount.isActive === 0) {
        console.log(`[updating account ws] Account ${accountIdentifier} marked as deleted (isActive=0)`);
        
        // Update state to remove the deleted account
        const remainingAccounts = accounts.filter(acc => acc.id !== accountIdentifier);
        useAccountPositionStore.setState(state => ({
          accounts: remainingAccounts,
          // Update pagination total elements when an account is deleted
          accountsPagination: {
            ...state.accountsPagination,
            totalElements: Math.max(0, state.accountsPagination.totalElements - 1)
          }
        }));
        
        // Handle account switching if the deleted account was currently selected
        if (selectedAccount === accountIdentifier) {
          if (remainingAccounts.length > 0) {
            const deletedIndex = accounts.findIndex(acc => acc.id === accountIdentifier);
            const nextAccountIndex = deletedIndex < remainingAccounts.length ? deletedIndex : remainingAccounts.length - 1;
            const nextAccount = remainingAccounts[nextAccountIndex];
            
            setSelectedAccount(nextAccount.id);
            console.log(`[updating account ws] Switched to next available account after deletion: ${nextAccount.id}`);
            
            if (token) {
              fetchAccountPositions(nextAccount.id, token);
              // Avoid showing loader for WS-triggered refresh
              clearLoading();
              console.log(`[updating account ws] Fetched positions for new selected account: ${nextAccount.id}`);
            }
          } else {
            // No accounts remaining, clear selected account and data
            setSelectedAccount(null);
            console.log('[updating account ws] No accounts remaining after deletion, cleared selection and positions');
            
            useAccountPositionStore.setState({
              positions: [],
              accountSummary: {
                totalPar: null,
                avgYield: null,
                average: null,
                interest: null,
                averageSPRating: null,
                taxable: null,
                totalCurrValue: null,
                avgYieldToMaturity: null,
                convexity: null,
                next12MonthPrincipal: null,
                next12MonthInterest: null,
                averageMoodys: null,
                nonTaxable: null,
                totalDv01: null,
              },
              pagination: {
                totalElements: 0,
                totalPages: 0,
                currentPage: 0,
                pageSize: 20
              }
            });
          }
        }

        // Only show modal if the deleted account is the currently viewed account
        const isViewingDeletedAccount = selectedAccount === accountIdentifier;
        console.log('[updating account ws] Is currently viewing deleted account:', isViewingDeletedAccount, 
          'selectedAccount:', selectedAccount, 'deletedAccount:', accountIdentifier);
        
        if (isViewingDeletedAccount) {
          const deletedAccountObj = accounts.find(acc => acc.id === accountIdentifier);
          
          // Only show modal for the active account being deleted
          console.log('[updating account ws] Showing delete modal for currently viewed account:', accountIdentifier);
          window.dispatchEvent(new CustomEvent('showAccountDeletedModal', { 
            detail: { 
              deletedAccount: deletedAccountObj || updatedAccount
            } 
          }));
        } else {
          console.log('[updating account ws] Skipping delete modal - account was not being viewed:', accountIdentifier);
        }
        
        return;
      }
      
      // Rest of the handleAccountUpdate function remains unchanged
      const existingAccount = accounts.find(acc => acc.id === accountIdentifier);
      const isNewAccount = !existingAccount;
      
      const formattedAccount = {
        id: accountIdentifier,
        accountId: accountIdentifier,
        // pmId: updatedAccount.pmId,
        userId: updatedAccount.userId,
        description: updatedAccount.description,
        custodianId: updatedAccount.custodianId,
        accountNumber: updatedAccount.accountIdentifier,
        accountIdentifier: updatedAccount.accountIdentifier,
        status: updatedAccount.status,
        isActive: updatedAccount.isActive === 1,
        entryBy: updatedAccount.entryBy || null,
      };
      
      console.log('[updating account ws] Formatted account for update:', formattedAccount);
      
      const currentSelectedAccount = selectedAccount;
      
      useAccountPositionStore.setState(state => {
        if (isNewAccount) {
          console.log('[updating account ws] Adding new account to list:', formattedAccount.id);
          const wasEmpty = state.accounts.length === 0;
          
          if (wasEmpty) {
            console.log('[updating account ws] First account added, auto-selecting:', formattedAccount.id);
            setTimeout(() => {
              setSelectedAccount(formattedAccount.id);
              if (token) {
                fetchAccountPositions(formattedAccount.id, token);
                // Avoid showing loader for WS-triggered refresh
                clearLoading();
                console.log('[updating account ws] Fetched positions for new account:', formattedAccount.id);
              }
            }, 100);
          }
          
          return {
            accounts: [...state.accounts, formattedAccount],
            selectedAccount: wasEmpty ? formattedAccount.id : currentSelectedAccount,
            // Update pagination total elements when a new account is added
            accountsPagination: {
              ...state.accountsPagination,
              totalElements: state.accountsPagination.totalElements + 1
            }
          };
        } else {
          console.log('[updating account ws] Updating existing account:', formattedAccount.id);
          return {
            accounts: state.accounts.map(acc => {
              if (acc.id === formattedAccount.id) {
                return {
                  ...acc,
                  ...formattedAccount,
                  positions: acc.positions || []
                };
              }
              return acc;
            }),
            selectedAccount: currentSelectedAccount
          };
        }
      });
      
      if (existingAccount && selectedAccount === accountIdentifier) {
        if (token) {
          fetchAccountPositions(accountIdentifier, token);
          // Avoid showing loader for WS-triggered refresh
          clearLoading();
          console.log('[updating account ws] Refreshed positions for updated selected account:', accountIdentifier);
        }
      }
    };

    // Handler for account summary update events
    // Now receives updates only for the currently selected account due to account-specific subscription in Requests.jsx
    const handleAccountSummaryUpdate = (event) => {
      // Clear loaders when WS update arrives
      clearLoading();

      const summary = event.detail;
      if (!summary) return;
      // [updating account ws] Received accountSummary event
      console.log('[updating account ws] Received accountSummary event:', summary);

      // Accept either accountId or accountMstId as the identifier
      const summaryAccountIdentifier = summary.accountId || summary.accountMstId;

      // Only update summary if the account is currently active
      if (summaryAccountIdentifier !== selectedAccount) {
        // [updating account ws] Ignored accountSummary event (not for active account)
        console.log('[updating account ws] Ignored accountSummary event (not for active account):', summaryAccountIdentifier);
        return;
      }

      const mappedSummary = {
        totalPar: summary.totalParValue ?? null,
        totalCurrValue: summary.totalCurrentValue ?? null,
        averageYieldToWorst: summary.averageYieldToWorst ?? null,
        avgYieldToMaturity: summary.averageYieldToMaturity ?? null,
        average: summary.averageDuration ?? null,
        convexity: summary.averageConvexity ?? null,
        interest: summary.interest ?? null,
        next12MonthPrincipal: summary.next12MonthPrincipal ?? null,
        next12MonthInterest: summary.next12MonthInterest ?? null,
        averageSPRating: summary.averageSPRating ?? null,
        averageMoodys: summary.averageMoodyRating ?? null,
        nonTaxable: summary.totalNonTaxable ?? null,
        taxable: summary.totalTaxable ?? null,
        totalDv01: summary.totalDv01 ?? null,
      };

      // [updating account ws] Updating account summary in store
      console.log('[updating account ws] Updating account summary in store:', mappedSummary);

      useAccountPositionStore.setState(state => ({
         accountSummary: {
          ...state.accountSummary,  // Keep existing fields (including accountInfo)
          ...mappedSummary,         // Update with new summary values
        }
      }));

      // Fetch updated positions after summary update
      if (token && summaryAccountIdentifier) {
        fetchAccountPositions(summaryAccountIdentifier, token);
        clearLoading(); // Clear loader after fetch
        console.log('[updating account ws] Fetched positions after summary update for account:', summaryAccountIdentifier);
      }

      // Optional: Log the updated store value after a tick
      setTimeout(() => {
        const currentSummary = useAccountPositionStore.getState().accountSummary;
        // [updating account ws] Account summary in store after update
        console.log('[updating account ws] Account summary in store after update:', currentSummary);
      }, 0);
    };

    // Handler for position update events
    // Now receives updates only for the currently selected account due to account-specific subscription in Requests.jsx
    const handlePositionUpdate = (event) => {
      // Clear loaders when WS update arrives
      clearLoading();

      const updatedPosition = event.detail;
      // [updating account ws] Received positionUpdate event
      console.log('[updating account ws] Received positionUpdate event:', updatedPosition);

      if (!updatedPosition || !updatedPosition.accountPositionMappingId || !updatedPosition.accountMstId) {
        // [updating account ws] Invalid position update data
        console.log('[updating account ws] Invalid position update data received:', updatedPosition);
        return;
      }

      // Accept either accountMstId or accountId as the identifier
      const positionAccountIdentifier = updatedPosition.accountMstId || updatedPosition.accountId;

      // Only update if the updated position belongs to the currently selected account
      if (selectedAccount === positionAccountIdentifier) {
        // [updating account ws] Updating position in table for selected account
        console.log(`[updating account ws] Updating position for account ${selectedAccount}, CUSIP: ${updatedPosition.cusip}`);

        // Update the positions array in the store
        useAccountPositionStore.setState(state => ({
          positions: state.positions.map(pos =>
            pos.accountPositionMappingId === updatedPosition.accountPositionMappingId
              ? {
                  ...pos,
                  ...updatedPosition,
                  securityWeightPercentage: updatedPosition.securityWeightPercentage ?? pos.securityWeightPercentage
                }
              : pos
          )
        }));

        // Optionally, update the position in the accounts array as well
        useAccountPositionStore.setState(state => ({
          accounts: state.accounts.map(acc => {
            if (acc.id === positionAccountIdentifier && Array.isArray(acc.positions)) {
              return {
                ...acc,
                positions: acc.positions.map(pos =>
                  pos.accountPositionMappingId === updatedPosition.accountPositionMappingId
                    ? {
                        ...pos,
                        ...updatedPosition,
                        securityWeightPercentage: updatedPosition.securityWeightPercentage ?? pos.securityWeightPercentage
                      }
                    : pos
                )
              };
            }
            return acc;
          })
        }));
      } else {
        // [updating account ws] Ignored position update (not for active account)
        console.log(`[updating account ws] Ignored position update for account ${positionAccountIdentifier} (not active)`);
      }
    };

    // Add event listeners
    window.addEventListener('accountUpdate', handleAccountUpdate);
    window.addEventListener('accountSummary', handleAccountSummaryUpdate);
    window.addEventListener('positionUpdate', handlePositionUpdate);

    // Cleanup function
    return () => {
      window.removeEventListener('accountUpdate', handleAccountUpdate);
      window.removeEventListener('accountSummary', handleAccountSummaryUpdate);
      window.removeEventListener('positionUpdate', handlePositionUpdate);
    };
  }, [accounts, selectedAccount, setSelectedAccount, setError, token, fetchAccountPositions, positions, userId]);
};
